import { Box, CircularProgress, Typography } from "@mui/material";
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import { useMemo, useState, useEffect, useRef } from "react";
import { imageQueue } from "../utils/imageQueue";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export function SecureImage({
  item,
  width = 80,
  height = 80,
  borderRadius = 8,
  objectFit = "contain",
  alt,
  showLoader = true,
  showFallbackIcon = true,
  maxRetries = 3,
  retryDelay = 1000,
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const imageSrc = useMemo(() => {
    if (!item?.imageUrl || !item?.imageKey || !item?.iv || !item?.authTag) {
      return null;
    }

    const params = new URLSearchParams({
      encryptedUrl: item.imageUrl,
      imageKey: item.imageKey,
      iv: item.iv,
      authTag: item.authTag,
    });

    // Add cache buster on retry
    if (retryCount > 0) {
      params.append("retry", retryCount.toString());
    }

    return `${API_BASE_URL}/api/fetchorder/secured-image?${params.toString()}`;
  }, [item, retryCount]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Load image through queue
  useEffect(() => {
    if (!imageSrc) return;

    // Reset states when imageSrc changes
    setLoaded(false);
    setError(false);

    const loadImage = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        const cleanup = () => {
          img.onload = null;
          img.onerror = null;
        };

        img.onload = () => {
          cleanup();
          resolve(img);
        };

        img.onerror = () => {
          cleanup();
          reject(new Error("Image load failed"));
        };

        img.src = imageSrc;
      });
    };

    // Add to queue
    imageQueue
      .add(loadImage)
      .then(() => {
        if (isMountedRef.current) {
          setLoaded(true);
          setError(false);
          setRetryCount(0);
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;

        if (retryCount < maxRetries) {
          // Retry with exponential backoff
          retryTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setRetryCount((prev) => prev + 1);
            }
          }, retryDelay * Math.pow(2, retryCount));
        } else {
          setError(true);
        }
      });

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [imageSrc, retryCount, maxRetries, retryDelay]);

  // FALLBACK (Broken image, missing encryption keys)
  if (!imageSrc || (error && retryCount >= maxRetries)) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f0f0f0",
        }}
      >
        {showFallbackIcon ? (
          <BrokenImageRoundedIcon
            sx={{
              color: "#999",
              fontSize: typeof width === "number" ? width * 0.45 : 36,
            }}
          />
        ) : (
          <Typography sx={{ color: "#aaa" }}>--</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius,
        overflow: "hidden",
        position: "relative",
        bgcolor: "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* LOADER */}
      {!loaded && showLoader && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <CircularProgress size={22} sx={{ color: "#777" }} />
          {retryCount > 0 && (
            <Typography
              sx={{
                marginTop: 1,
                fontSize: 10,
                color: "#999",
              }}
            >
              Retry {retryCount}/{maxRetries}
            </Typography>
          )}
        </Box>
      )}

      {/* IMAGE */}
      <img
        src={imageSrc}
        alt={alt || "image"}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit,
          objectPosition: "center",
          borderRadius,
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.25s ease",
          display: "block",
        }}
      />
    </Box>
  );
}
